import { PanelHeader } from '@app/components/base/content-structure/PanelHeader';
import { RangeWaveformPlayer } from '@app/core/playback/audio/range-waveform-player/RangeWaveformPlayer';
import { VideoJsPlaybackProvider } from '@app/core/playback/video/VideoPlaybackProvider';
import { isVideoStation } from '@app/core/stations/helpers';
import { Station } from '@app/core/types/stations';
import { PlaybackVideoJsPlayer } from '@app/core/video-player/PlaybackVideoJsPlayer';
import { ShowOnStreamAction } from '@app/modules/audit/components/ShowOnStreamAction';
import { buildStationClipHlsUrl } from '@app/modules/logger/screens/stream/helpers/build-station-clip-hls-url';
import { Box } from '@mui/material';
import { defineMessage, FormattedMessage } from 'react-intl';
import { VERIFY_MATCH_BROADCAST_PLAYBACK_ID } from './verify-match-constants';
import { VerifyMatchPlaybackRange } from './verify-match-range';
import {
  getVerifyMatchBroadcastDurationMs,
  getVerifyMatchDatetime,
  getVerifyMatchLoggerTab,
} from './verify-match-target-utils';
import { VerifyMatchTarget } from './verify-match-types';

const broadcastTitleMessage = defineMessage({
  id: 'audit.verifyMatch.broadcastTitle',
  defaultMessage: 'Emisión detectada',
});

export interface VerifyMatchBroadcastPlayerProps {
  station: Station;
  target: VerifyMatchTarget;
  range: VerifyMatchPlaybackRange;
  /**
   * Optional progressive/HLS video URL. When set on a video station, skips the
   * clipped station HLS playlist (useful for Storybook demos).
   */
  videoSrc?: string;
}

/**
 * Plays the station broadcast for the match time range using HLS (video) or a waveform player (audio).
 * Section header includes a go-to-logger action for the same airing.
 */
export function VerifyMatchBroadcastPlayer({
  station,
  target,
  range,
  videoSrc,
}: VerifyMatchBroadcastPlayerProps) {
  const { from, to } = range;
  const isVideo = isVideoStation(station);
  const rangeKey = `${from}-${to}`;
  const datetime = getVerifyMatchDatetime(target);
  const loggerTab = getVerifyMatchLoggerTab(target);
  const durationMs = getVerifyMatchBroadcastDurationMs(target, range);
  const src = videoSrc ?? buildStationClipHlsUrl(station.id, from, to);

  return (
    <Box>
      <PanelHeader
        title={<FormattedMessage {...broadcastTitleMessage} />}
        divider={false}
        actions={
          <ShowOnStreamAction
            station={station.slug}
            datetime={datetime}
            durationMs={durationMs}
            tab={loggerTab}
          />
        }
      />

      {isVideo ? (
        <VideoJsPlaybackProvider
          key={videoSrc ? `demo-${videoSrc}` : rangeKey}
          playbackId={VERIFY_MATCH_BROADCAST_PLAYBACK_ID}
          exclusivePlaybackMode="pause"
          autoPlay
          src={src}
          // Demo progressive files are not station-time clipped playlists.
          playbackRange={videoSrc ? undefined : { from, to }}
        >
          <PlaybackVideoJsPlayer bordered />
        </VideoJsPlaybackProvider>
      ) : (
        <RangeWaveformPlayer
          station={station}
          from={from}
          to={to}
          playbackId={VERIFY_MATCH_BROADCAST_PLAYBACK_ID}
          exclusivePlaybackMode="pause"
          playingAction="stop"
          autoPlay
        />
      )}
    </Box>
  );
}
